import React, { useState, useEffect, useCallback } from 'react';
import {
  Expandable,
  getRehydratableName,
  getClassNameFactory,
  Typography,
  ListOfLinks,
} from '@dcl/ui';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography/TypographyTypes';
import { IProductSelectionProps } from './types';
import { Responsive } from '@dcl/ui/dist/modules';
import { MobileView } from './components';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { filterProduct, filterFn } from './helpers/filterProduct';
import { MOBILE_MAX_WIDTH, DESKTOP_MIN_WIDTH } from './helpers/constants';
import {
  getModifiedListDesktopData,
  getProductListForDropdown,
} from './helpers/ProductSelectionUtils';
import { ExpandableContent } from './components/ExpandableContent';
import { IHeaderProps } from '@dcl/ui/dist/modules/Expandable/components/Header/HeaderTypes';
import './index.css';

export const ProductSelection = (props: IProductSelectionProps) => {
  const {
    productList,
    submitButtonText,
    showButtonText,
    hideButtonText,
    browseLabel,
    dropdownLabel,
    chooseProductLabel,
    headerLabel,
  } = props;
  const [productLink, setProductLink] = useState('');
  const [showError, setShowError] = useState(false);
  const [mobileInputValue, setMobileInputValue] = useState('');
  const [mobileProductList, setMobileProductList] = useState<any>([]);
  const dropdownProductList = getProductListForDropdown(productList);
  const modifiedProductListDesktopData = getModifiedListDesktopData(
    productList
  );

  const handleValueChange = (value: string) => {
    setProductLink(filterProduct(value, productList));
    setShowError(false);
  };

  const handleClearButtonClick = () => {
    setProductLink('');
  };

  const handleFilterValueChange = (value: string) => {
    if (
      productList.find(
        (product) =>
          product.label.toLocaleLowerCase() === value.toLocaleLowerCase()
      )
    ) {
      setProductLink(filterProduct(value, productList));
    } else {
      setProductLink('');
    }
  };

  const handleMobileInputChange = useCallback(
    (value) => {
      setMobileInputValue(value);
      setMobileProductList(filterFn(productList, value));
    },
    [productList]
  );

  const handleSubmit = () => {
    setShowError(!productLink);
  };

  const expandProductSelection = (
    headerProps: IHeaderProps & {
      title: React.ReactNode;
    }
  ) => {
    return (
      <ExpandableContent
        showError={showError}
        handleFilterValueChange={handleFilterValueChange}
        handleClearButtonClick={handleClearButtonClick}
        handleValueChange={handleValueChange}
        productLink={productLink}
        handleSubmit={handleSubmit}
        submitButtonText={submitButtonText}
        hideButtonText={hideButtonText}
        showButtonText={showButtonText}
        headerProps={headerProps}
        getClassName={getClassName}
        dropdownLabel={dropdownLabel}
        dropdownProductList={dropdownProductList}
        browseLabel={browseLabel}
      />
    );
  };

  useEffect(() => setMobileProductList(productList), [productList]);
  const getClassName = getClassNameFactory(ProductSelection.displayName);
  return (
    <div
      data-rehydratable={getRehydratableName(ProductSelection.displayName)}
      {...propsToDataAttributes(props)}
    >
      <Responsive minWidth={0} maxWidth={MOBILE_MAX_WIDTH}>
        <MobileView
          mobileProductList={mobileProductList}
          handleMobileInputChange={handleMobileInputChange}
          mobileInputValue={mobileInputValue}
          browseLabel={browseLabel}
          buttonLabel={dropdownLabel}
        />
      </Responsive>
      <Responsive minWidth={DESKTOP_MIN_WIDTH}>
        <Expandable
          isExpanded={false}
          className={getClassName({ descendantName: 'container' })}
          header={(headerProps) => expandProductSelection(headerProps)}
        >
          <div className={getClassName({ descendantName: 'listContainer' })}>
            <div className={getClassName({ descendantName: 'products' })}>
              <Typography
                className={getClassName({
                  descendantName: 'allProducts',
                })}
              >
                {headerLabel}
              </Typography>
              <Typography
                className={getClassName({
                  descendantName: 'containerTypography',
                })}
                tag={TypographyTags.P}
              >
                {chooseProductLabel}
              </Typography>
              {modifiedProductListDesktopData?.map((listItem, index) => {
                const keyValue = `listItem_${index + 1}`;
                return (
                  <ListOfLinks.Column key={keyValue}>
                    <div
                      className={getClassName({
                        descendantName: 'list',
                      })}
                    >
                      <ListOfLinks.Link>{listItem.label}</ListOfLinks.Link>
                    </div>
                    {listItem.items?.map((item) => {
                      return (
                        item.value && (
                          <div
                            className={getClassName({
                              descendantName: 'listElement',
                            })}
                          >
                            <ListOfLinks.Link href={item.value}>
                              {item.label}
                            </ListOfLinks.Link>
                          </div>
                        )
                      );
                    })}
                  </ListOfLinks.Column>
                );
              })}
            </div>
          </div>
        </Expandable>
      </Responsive>
    </div>
  );
};

ProductSelection.displayName = 'ProductSelection';
